import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { forecastChartConfig } from '../../features/ForecastCharts/config/forecastCharts.config';
import * as chartUtils from '../../features/ForecastCharts/utils/forecastChartGenerators.utils';
import useLocation from 'hooks/useLocation';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import {
  useGet15DayForecastQuery,
  useGet7DayHourlyForecastQuery,
  useGetDetailedForecastQuery,
} from 'services/weather/weather.api';
import { selectResolvedThemeTokens } from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import CombinedChartContent from './components/CombinedChartContent/CombinedChartContent';
import { getCombinedChartDefinition, route } from './config/combinedChartsWidget.config';
import './CombinedChartsWidget.scss';

const build15DayChartDataById = (day15Forecast, textFillStyle) => {
  if (!Array.isArray(day15Forecast) || day15Forecast.length === 0) return {};

  const generatedChartData = {
    day15Forecast: chartUtils.generate15DayChart(
      day15Forecast,
      { legend: { display: false } },
      { color: 'black' },
      { color: 'black' },
      textFillStyle,
    ),
  };

  const configuredChartData = Object.fromEntries(
    forecastChartConfig
      .filter(({ sourceKey, datasets }) => sourceKey === 'day15Forecast' && datasets)
      .map((definition) => [
        definition.id,
        chartUtils.generateMetricChart(day15Forecast, definition),
      ]),
  );

  return { ...generatedChartData, ...configuredChartData };
};

// All tabs work with each instance of this widget. `defaultTab` determines which
// chart opens when the widget first renders and when the user enters fullscreen.
export default function CombinedChartsWidget({
  defaultTab,
  widgetRefresh,
  dimensions,
  containerSizePx,
}) {
  const { locations } = useLocation();
  const weatherLocation = locations.weatherLocation;
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const themeTokens = useSelector(selectResolvedThemeTokens);
  const textFillStyle = themeTokens.fontColor || 'black';
  const {
    currentData: detailedForecast,
    isLoading: isDetailedForecastLoading,
    isFetching: isDetailedForecastFetching,
    isError: isDetailedForecastError,
    error: detailedForecastError,
    refetch: refetchDetailedForecast,
    fulfilledTimeStamp: detailedUpdatedAt,
  } = useGetDetailedForecastQuery(weatherLocation);
  const {
    currentData: day7Forecast,
    isLoading: isDay7ForecastLoading,
    isFetching: isDay7ForecastFetching,
    isError: isDay7ForecastError,
    error: day7ForecastError,
    refetch: refetchDay7Forecast,
    fulfilledTimeStamp: hourlyUpdatedAt,
  } = useGet7DayHourlyForecastQuery(weatherLocation);
  const {
    currentData: day15Forecast,
    isLoading: isDay15ForecastLoading,
    isFetching: isDay15ForecastFetching,
    isError: isDay15ForecastError,
    error: day15ForecastError,
    refetch: refetchDay15Forecast,
    fulfilledTimeStamp: day15UpdatedAt,
  } = useGet15DayForecastQuery(weatherLocation);

  const detailedData = useMemo(() => {
    if (!Array.isArray(detailedForecast) || detailedForecast.length === 0) return undefined;

    return chartUtils.generateDetailedChart(
      detailedForecast,
      { legend: { display: false } },
      { color: 'black' },
      { color: 'black' },
      textFillStyle,
    );
  }, [detailedForecast, textFillStyle]);

  const day7Data = useMemo(() => {
    if (!Array.isArray(day7Forecast) || day7Forecast.length === 0) return undefined;

    const chart = chartUtils.generate7DayChart(
      day7Forecast,
      { legend: { display: false } },
      6,
      4,
      { color: 'black' },
      { color: 'black' },
      textFillStyle,
    );

    // Give every hour a label so ECharts can choose readable ticks at any width or zoom level.
    chart.options.scales.x.labels = day7Forecast.map(({ iso8601 }) =>
      new Date(iso8601).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    );
    return chart;
  }, [day7Forecast, textFillStyle]);

  const day15ChartDataById = useMemo(
    () => build15DayChartDataById(day15Forecast, textFillStyle),
    [day15Forecast, textFillStyle],
  );

  const chartStyle = useMemo(
    () => ({
      backgroundColor: 'rgba(0,0,0,0)',
      color: textFillStyle,
      height: '100%',
    }),
    [textFillStyle],
  );

  useEffect(() => {
    setSelectedTab(defaultTab);
  }, [defaultTab]);

  const retryFetch = () => {
    if (!weatherLocation?.city || !weatherLocation?.state) return;

    refetchDetailedForecast();
    refetchDay7Forecast();
    refetchDay15Forecast();
  };

  const chartDataById = {
    detailedForecast: detailedData,
    hourlyForecast: day7Data,
    ...day15ChartDataById,
  };

  const sourceStateByKey = {
    detailedForecast: {
      isLoading: isDetailedForecastLoading || isDetailedForecastFetching,
      isError: isDetailedForecastError,
      error: detailedForecastError,
    },
    hourlyForecast: {
      isLoading: isDay7ForecastLoading || isDay7ForecastFetching,
      isError: isDay7ForecastError,
      error: day7ForecastError,
    },
    day15Forecast: {
      isLoading: isDay15ForecastLoading || isDay15ForecastFetching,
      isError: isDay15ForecastError,
      error: day15ForecastError,
    },
  };

  const selectedDefinition = getCombinedChartDefinition(selectedTab);
  const updatedAtBySource = {
    detailedForecast: detailedUpdatedAt,
    hourlyForecast: hourlyUpdatedAt,
    day15Forecast: day15UpdatedAt,
  };
  const selectedChart = {
    chartData: chartDataById[selectedDefinition.id],
    type: selectedDefinition.chartType,
    ...sourceStateByKey[selectedDefinition.sourceKey],
  };

  if (selectedChart.isError) {
    return (
      <ErrorMessage
        message={getApiErrorMessage(selectedChart.error)}
        retryRoute={route}
        onRetry={retryFetch}
      />
    );
  }

  return (
    <div className="combined-charts-widget d-flex flex-column h-100 min-h-0 min-w-0 overflow-hidden">
      <WidgetTimeUpdate
        route={route}
        compact
        updatedAt={updatedAtBySource[selectedDefinition.sourceKey] ?? null}
      />
      <div className="d-flex flex-column flex-grow-1 min-h-0 min-w-0 gap-2 p-2 overflow-hidden">
        {/* Open the menu outside the widget so its overflow rules cannot clip the choices. */}
        <BaseSelect
          wrapperClassName="flex-shrink-0 min-w-0"
          classNames={{ control: () => 'combined-charts-selector' }}
          styles={{
            menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 'var(--z-popover)' }),
          }}
          isSearchable
          isClearable={false}
          selectProps={{
            'aria-label': 'Chart',
            options: forecastChartConfig,
            getOptionLabel: (definition) => definition.optionLabel,
            getOptionValue: (definition) => definition.id,
            value: selectedDefinition,
            onChange: (definition) => setSelectedTab(forecastChartConfig.indexOf(definition)),
            menuPosition: 'fixed',
            menuPlacement: 'auto',
            menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
          }}
        />
        <div className="d-flex flex-grow-1 flex-basis-0 min-h-0 min-w-0">
          <CombinedChartContent
            chartId={selectedDefinition.id}
            chartData={selectedChart.chartData}
            chartStyle={chartStyle}
            dimensions={dimensions}
            isLoading={selectedChart.isLoading}
            type={selectedChart.type}
            containerWidth={containerSizePx?.width}
          />
        </div>
      </div>
    </div>
  );
}
